import { useTranslation } from 'react-i18next';
import { ArrowUpRight, ArrowDownRight, Play } from 'lucide-react';
import type { ProNotification } from '@erp/data-access/interfaces';
import {
  buildNotificationDisplay,
  formatRelativeTime,
} from '@/lib/notification-view';
import { OrderProgress } from './OrderProgress';

/**
 * Une ligne du fil de notifications.
 *
 * Trois couches d'information, de la plus rapide à lire à la plus détaillée :
 * la pastille colorée (de quoi s'agit-il), le montant en gros s'il y en a un,
 * puis les puces de chiffres. Une notification sans metadata ne montre que le
 * texte — la mise en page ne s'effondre pas.
 */
export function NotificationItem({
  notification,
  now,
  onSelect,
}: {
  notification: ProNotification;
  now: Date;
  onSelect?: (notification: ProNotification) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'fr';
  const display = buildNotificationDisplay(notification, t, locale);
  const { view, palette, Icon } = display;
  const unread = notification.readAt === null;

  const Wrapper = onSelect ? 'button' : 'div';

  return (
    <Wrapper
      {...(onSelect
        ? { type: 'button' as const, onClick: () => onSelect(notification) }
        : {})}
      className={`w-full text-left block px-4 py-3 transition-colors ${
        onSelect ? 'hover:bg-neutral-50 cursor-pointer' : ''
      }`}
      style={{
        borderBottom: '1px solid var(--pro-line)',
        background: unread ? 'rgba(255,107,30,0.045)' : 'transparent',
        // Liseré de bord côté début de ligne : survit au RTL, contrairement à
        // un borderLeft en dur.
        borderInlineStart: unread
          ? '3px solid var(--pro-primary)'
          : '3px solid transparent',
      }}
      aria-label={unread ? t('pro.notif.unread', 'Non lue') : undefined}
    >
      <div className="flex items-start gap-3">
        {/* Pastille — ou vignette vidéo quand il y en a une */}
        {view.thumbnailUrl ? (
          <span className="relative w-11 h-11 rounded-xl overflow-hidden shrink-0 bg-neutral-100">
            <img
              src={view.thumbnailUrl}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/30">
              <Play className="w-4 h-4 text-white fill-white" />
            </span>
          </span>
        ) : (
          <span
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: palette.bg }}
          >
            <Icon className="w-[18px] h-[18px]" style={{ color: palette.fg }} aria-hidden />
          </span>
        )}

        <span className="flex-1 min-w-0 block">
          <span className="flex items-start justify-between gap-2">
            <span
              className={`text-[13px] leading-snug block ${
                unread ? 'font-semibold' : 'font-medium'
              }`}
              style={{
                color: 'var(--pro-text-dark)',
                fontFamily: 'Figtree',
              }}
            >
              {display.title}
            </span>
            <time
              dateTime={notification.createdAt}
              className="text-[11px] shrink-0 whitespace-nowrap mt-0.5"
              style={{ color: 'var(--pro-text-grey-light)' }}
            >
              {formatRelativeTime(notification.createdAt, locale, now)}
            </time>
          </span>

          {display.amountLabel && (
            <span
              className="block text-[17px] font-bold leading-tight mt-1 tabular-nums"
              style={{ color: palette.fg, fontFamily: 'Figtree' }}
            >
              {display.amountLabel}
            </span>
          )}

          {display.body && (
            <span
              className="block text-[12px] leading-snug mt-0.5 line-clamp-2"
              style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
            >
              {display.body}
            </span>
          )}

          {view.orderProgress && <OrderProgress progress={view.orderProgress} />}

          {display.stats.length > 0 && (
            <span className="flex flex-wrap gap-1.5 mt-2">
              {display.stats.map((stat) => (
                <span
                  key={stat.id}
                  className="inline-flex items-baseline gap-1 px-2 py-[3px] rounded-lg text-[11px] leading-none"
                  style={{ background: stat.palette.bg }}
                >
                  <span style={{ color: 'var(--pro-text-grey)' }}>
                    {stat.label}
                  </span>
                  <span
                    className="font-semibold tabular-nums inline-flex items-center gap-0.5"
                    style={{ color: stat.palette.fg }}
                  >
                    {stat.value}
                    {stat.trend === 'up' && (
                      <ArrowUpRight className="w-3 h-3" aria-hidden />
                    )}
                    {stat.trend === 'down' && (
                      <ArrowDownRight className="w-3 h-3" aria-hidden />
                    )}
                  </span>
                </span>
              ))}
            </span>
          )}
        </span>
      </div>
    </Wrapper>
  );
}
